import axios from "axios";
import { isLocalDev } from "../utils/environment";
import { toast } from "react-toastify";

// We'll store these functions here after they are initialized from the React context
let showLoadingGlobal = () => {};
let hideLoadingGlobal = () => {};
let getCsrfToken = () => null; // function provided by AuthContext
let navigateGlobal = null; // navigation function for redirects
let logoutGlobal = () => {}; // logout function from AuthContext

// (Removed unused showErrorGlobal placeholder to reduce noise)

// Function to set the global loading handlers
export const setLoadingHandlers = (showFn, hideFn) => {
  showLoadingGlobal = showFn;
  hideLoadingGlobal = hideFn;
};

// Function to set the global navigation handler
export const setNavigationHandler = (navigateFn) => {
  navigateGlobal = navigateFn;
};

// Function to set the global logout handler
export const setLogoutHandler = (logoutFn) => {
  logoutGlobal = logoutFn;
};

export const setCsrfTokenGetter = (getterFn) => {
  getCsrfToken = getterFn;
};

// This will be showErrorToast from ToastProvider
export const setErrorHandler = (showErrorFn) => {
  // deprecated placeholder removed
};

const client = axios.create({
  baseURL: process.env.REACT_APP_API_END_POINT,
});

// Attach a custom per-request meta container
client.interceptors.request.use(
  config => {
    showLoadingGlobal();
    // Ensure meta object exists for downstream logic (e.g., suppressAuthError)
    config.meta = config.meta || {};
    // Attach CSRF token automatically for unsafe methods if available
    const method = (config.method || 'get').toUpperCase();
    if (['POST','PUT','PATCH','DELETE'].includes(method)) {
      const token = getCsrfToken();
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers['x-csrf-token']) {
          config.headers['x-csrf-token'] = token;
        }
      }
    }
    return config;
  },
  error => {
    hideLoadingGlobal();
    toast.error("Network request failed.", error.message);
    return Promise.reject(error);
  }
);

// --- AXIOS INTERCEPTORS ---

// Response Interceptor
client.interceptors.response.use(
  response => {
    hideLoadingGlobal(); // Hide spinner on successful response
    // Your existing logic for unwrapping data based on responseType
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response;
    }

    // Check for server-defined 'error' code in successful HTTP responses (e.g., 200 OK with code:1)
    if (response.data && response.data.code === 1) {
        hideLoadingGlobal();
        const serverMessage = response.data.message || "An unknown error occurred.";
        const serverErrors = response.data.errors;

        let fullErrorMessage = serverMessage;
        if (serverErrors && Array.isArray(serverErrors) && serverErrors.length > 0) {
            fullErrorMessage += ": " + serverErrors.map(d => d.message || d).join(', ');
        } else if (serverErrors && typeof serverErrors === 'string') {
            fullErrorMessage += ": " + serverErrors;
        }

        toast.error(fullErrorMessage); // Use react-toastify for logical errors

        // Reject the promise so that the calling component's .catch() block is still triggered.
        return Promise.reject({
            response: {
                status: 200, // Still 200 OK HTTP status
                data: response.data // Pass original data for custom handling in component
            }
        });
    }

    // If it's a true success (code: 0), we might optionally show a success toast here
    // toast.success(response.data.message || "Operation successful!");    
    return response.data;
  },
  error => {
    hideLoadingGlobal(); // Hide spinner on error response
    
    // Check if the request was cancelled/aborted
    if (axios.isCancel(error)) {
      // Request was cancelled - don't show error toast
      if (isLocalDev) {
        console.log("Request cancelled:", error.message);
      }
      return Promise.reject(error);
    }

    let errorMessage = "An unexpected error occurred.";
  // let errorDetails = null; // reserved for future detailed logging

    // Your existing error handling logic
    if (error.response) {

      // Server responded with a status code that falls out of the range of 2xx
      if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
        
        // Append detailed error messages if available
        const serverErrors = error.response.data.errors;
        if (serverErrors && Array.isArray(serverErrors) && serverErrors.length > 0) {
          const detailedMessages = serverErrors.map(err => err.message || err).join(', ');
          errorMessage += ': ' + detailedMessages;
        } else if (serverErrors && typeof serverErrors === 'string') {
          errorMessage += ': ' + serverErrors;
        }
      } else {
        errorMessage = `Server Error: ${error.response.status}`;
      }

      if (isLocalDev) {
        console.error("Interceptor Error - Status:", error.response.status);
        console.error("Interceptor Error - Data:", error.response.data);
        console.error("Interceptor Error - Headers:", error.response.headers);
      }
    } else if (error.request) {
            errorMessage = "No response from server. Please check your internet connection or try again later.";

      if (isLocalDev) {
        console.error("Interceptor Error - No response received:", error.request);
      }
    } else {
      errorMessage = `Request Error: ${error.message}`;
      if (isLocalDev) {
        console.error("Interceptor Error - Request setup:", error.message);
      }
    }

    // Determine if we should suppress standard unauthorized toast
    const originalConfig = error.config || {};
    const suppressAuthError = originalConfig.meta?.suppressAuthError;
    const suppressErrorToast = originalConfig.meta?.suppressErrorToast;
    const status = error.response?.status;
    const isAuthMe = originalConfig.url === '/auth/me';
    const isAuthLogin = originalConfig.url === '/auth/login';
    const isAuthLogout = originalConfig.url === '/auth/logout';
    const shouldSuppress = suppressAuthError && status === 401 && (isAuthLogin || isAuthMe);

    // Handle 401 Unauthorized - session expired
    if (status === 401 && !isAuthLogin && !isAuthMe && !isAuthLogout) {
      // Session expired - logout and redirect to login
      if (logoutGlobal) {
        logoutGlobal();
      }
      if (navigateGlobal) {
        const currentPath = window.location.pathname + window.location.search;
        // Don't preserve /login or /forbidden paths
        const shouldPreservePath = currentPath !== '/login' && currentPath !== '/forbidden';
        navigateGlobal('/login', { 
          replace: true,
          state: shouldPreservePath ? { from: currentPath } : undefined
        });
      }
      toast.error('Your session has expired. Please login again.');
    } else if (!shouldSuppress) {
      if (!suppressErrorToast) {
        toast.error(errorMessage);
      }
    }

    //showErrorGlobal(errorMessage, errorDetails);    
    return Promise.reject(error);
  }
);

// --- END AXIOS INTERCEPTORS ---

export function get(options) {
  options['method'] = 'GET';

  return request(options);
}

export function put(options) {
  options['method'] = 'PUT';
  return request(options);
}

function request(options, onUploadProgress) {

  // const onSuccess = function (response) {
  //   // If the responseType was 'blob' or 'arraybuffer', we need the full response object
  //   // including headers to get things like Content-Disposition.
  //   if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
  //     return response; // Return the full axios response object
  //   }
    
  //   // For other response types (e.g., 'json', 'text'), return only the data
  //   return response.data;
  // };

  // const onFailure = function (error) {
  //   //console.error("Request failed", error);

  //   if (error.response) {
  //     // Request was made but server responded with something other than 2xx
  //     if (isLocalDev) {
  //       console.error("Status:", error.response.status);
  //       console.error("Data:", error.response.data);
  //       console.error("Headers:", error.response.headers);
  //     }
  //   } else {
  //     // Something else happened while setting up the request triggered the error
  //     if (isLocalDev) {
  //       console.error("Error Message:", error.message);
  //     }
  //   }

  //   return Promise.reject(error.response || error.message);
  // };

  // Add the `onUploadProgress` function to the request if provided
  if (onUploadProgress) {
    options.onUploadProgress = onUploadProgress;
  }

  // Execute the Axios request and chain the custom success/failure handlers
  // Allow caller to set suppression flags; map to axios config.meta
  const { suppressAuthError, suppressErrorToast, ...rest } = options;
  return client({ ...rest, meta: { suppressAuthError, suppressErrorToast } });
}

export default request;